import { useCallback, useEffect, useRef, useState } from "react"

const SEARCH_DEBOUNCE_MS = 1000

function buildQueryString(params) {
  const usp = new URLSearchParams()
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "") return
    if (Array.isArray(v)) {
      v.forEach(item => usp.append(k, item))
    } else {
      usp.append(k, v)
    }
  })
  return usp.toString()
}

// Kick each card's primary photo into the browser's HTTP cache so <img> tags
// on the next page render from disk instead of round-tripping to origin. No-op
// on SSR (no window). new Image() with .src is enough — the browser fetches
// the resource and the response gets cached under normal HTTP caching rules.
function warmImages(json) {
  if (typeof window === "undefined" || !json || !Array.isArray(json.data)) return
  for (const row of json.data) {
    const url = row && row.primary_photo_url
    if (!url) continue
    try {
      const img = new window.Image()
      img.decoding = "async"
      img.src = url
    } catch (_e) {
      // ignore — this is best-effort
    }
  }
}

function paramsEqual(a, b) {
  const aKeys = Object.keys(a).sort()
  const bKeys = Object.keys(b).sort()
  if (aKeys.length !== bKeys.length) return false
  for (let i = 0; i < aKeys.length; i++) {
    if (aKeys[i] !== bKeys[i]) return false
    if (JSON.stringify(a[aKeys[i]]) !== JSON.stringify(b[aKeys[i]])) return false
  }
  return true
}

/**
 * useListingsSearch — debounced, cursor-paginated fetch against /api/properties,
 * with next-page prefetch so "Load more" feels instant.
 *
 * params: object of filters/search the hook forwards to the API
 *   (e.g. { search, county, property_type, price_min, price_max, sort_by, sort_order, limit }).
 *   Changing params clears the result list, invalidates any prefetch, and
 *   refetches from cursor=null.
 *
 * Returns { items, isLoading, isLoadingMore, hasMore, loadMore, error, total, bounds }.
 */
export default function useListingsSearch(params = {}) {
  const [items, setItems] = useState([])
  const [cursor, setCursor] = useState(null)
  const [hasMore, setHasMore] = useState(false)
  // Start in the loading state so the UI shows a spinner from first render
  // through the debounce window and into the actual fetch — no empty-state
  // flash before the first request lands.
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [error, setError] = useState(null)
  const [total, setTotal] = useState(null)
  const [bounds, setBounds] = useState(null)

  const lastParamsRef = useRef({})
  const requestIdRef = useRef(0)
  const debounceTimerRef = useRef(null)
  // Prefetch record: { cursor, params, promise, result, aborted }.
  // - cursor:  the cursor we're prefetching for (matches what loadMore will need)
  // - params:  a snapshot of filters at prefetch time (must match on consume)
  // - promise: in-flight fetch (awaited if the user clicks before it lands)
  // - result:  parsed JSON once resolved, or null if not yet / errored
  // - aborted: set true when invalidated (filter change, chain reset)
  const prefetchRef = useRef(null)

  const invalidatePrefetch = useCallback(() => {
    if (prefetchRef.current) prefetchRef.current.aborted = true
    prefetchRef.current = null
  }, [])

  // Fire a background fetch for the next cursor. Silent on error — the user
  // only sees a problem if they actually click Load More (loadMore falls back
  // to a fresh runFetch in that case). Also warms the browser's HTTP cache
  // for each card's primary photo so the thumbnails don't cold-load when the
  // rows hit the DOM.
  const startPrefetch = useCallback((nextCursor, effectiveParams) => {
    if (!nextCursor) return
    const qs = buildQueryString({
      ...effectiveParams,
      cursor: nextCursor,
    })
    const record = {
      cursor: nextCursor,
      params: effectiveParams,
      result: null,
      aborted: false,
      promise: null,
    }
    record.promise = fetch(`/api/properties?${qs}`)
      .then(res => (res.ok ? res.json() : null))
      .then(json => {
        if (record.aborted) return null
        record.result = json
        warmImages(json)
        return json
      })
      .catch(() => null)
    prefetchRef.current = record
  }, [])

  // Apply a resolved page response to state, and chain the next prefetch.
  const applyPage = useCallback(
    (json, effectiveParams, append) => {
      const next = json && json.pagination ? json.pagination.next_cursor : null
      setCursor(next || null)
      setHasMore(Boolean(next))
      setTotal(json && json.pagination ? json.pagination.total : null)
      setBounds(json && json.pagination ? json.pagination.bounds : null)
      setItems(prev =>
        append ? [...prev, ...((json && json.data) || [])] : (json && json.data) || []
      )
      if (next) startPrefetch(next, effectiveParams)
    },
    [startPrefetch]
  )

  const runFetch = useCallback(
    async (effectiveParams, append, cursorArg) => {
      const myId = ++requestIdRef.current
      const qs = buildQueryString({
        ...effectiveParams,
        cursor: cursorArg || undefined,
      })
      if (append) setIsLoadingMore(true)
      else setIsLoading(true)
      setError(null)
      try {
        const res = await fetch(`/api/properties?${qs}`)
        if (!res.ok) {
          const body = await res.text()
          throw new Error(`HTTP ${res.status}: ${body.slice(0, 200)}`)
        }
        const json = await res.json()
        if (myId !== requestIdRef.current) return
        applyPage(json, effectiveParams, append)
      } catch (err) {
        if (myId !== requestIdRef.current) return
        setError(err.message || String(err))
        if (!append) setItems([])
      } finally {
        if (myId === requestIdRef.current) {
          setIsLoading(false)
          setIsLoadingMore(false)
        }
      }
    },
    [applyPage]
  )

  useEffect(() => {
    if (paramsEqual(params, lastParamsRef.current)) return
    invalidatePrefetch()
    lastParamsRef.current = params
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
    // Show the loading state through the debounce window; runFetch keeps it
    // set once the request actually goes out.
    setIsLoading(true)
    setError(null)
    debounceTimerRef.current = setTimeout(() => {
      runFetch(params, false, null)
    }, SEARCH_DEBOUNCE_MS)
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
    }
  }, [params, runFetch, invalidatePrefetch])

  const loadMore = useCallback(async () => {
    if (!hasMore || isLoadingMore || isLoading) return
    const target = cursor
    const activeParams = lastParamsRef.current
    const prefetch = prefetchRef.current
    const canUsePrefetch =
      prefetch &&
      !prefetch.aborted &&
      prefetch.cursor === target &&
      paramsEqual(prefetch.params, activeParams)

    if (!canUsePrefetch) {
      runFetch(activeParams, true, target)
      return
    }

    // Consume the prefetch. Bump the request id so a mid-await filter change
    // invalidates cleanly, and clear the ref so we don't double-consume it.
    prefetchRef.current = null
    const myId = ++requestIdRef.current
    setIsLoadingMore(true)
    setError(null)
    try {
      const json = prefetch.result || (await prefetch.promise)
      if (myId !== requestIdRef.current) return
      if (!json) {
        // prefetch failed silently — fall back to a fresh fetch
        runFetch(activeParams, true, target)
        return
      }
      applyPage(json, activeParams, true)
    } catch (err) {
      if (myId !== requestIdRef.current) return
      // Prefetch threw — fall back
      runFetch(activeParams, true, target)
    } finally {
      if (myId === requestIdRef.current) setIsLoadingMore(false)
    }
  }, [hasMore, isLoadingMore, isLoading, cursor, runFetch, applyPage])

  return {
    items,
    isLoading,
    isLoadingMore,
    hasMore,
    loadMore,
    error,
    total,
    bounds,
  }
}
